import { Autocomplete, AutocompleteOption, Stack, Typography } from "@mui/joy";
import type { Transcript } from "../../types/transcripts";

interface TranscriptSourcePickerProps {
  transcripts: Transcript[];
  value: Transcript | null;
  disabled: boolean;
  loading: boolean;
  onSearchChange: (value: string) => void;
  onSelect: (transcript: Transcript | null) => void;
}

const transcriptLabel = (transcript: Transcript) => {
  const date = new Date(transcript.created_date).toLocaleDateString();
  return (
    transcript.title +
    " · " +
    date +
    " · " +
    (transcript.folder_name || "No folder")
  );
};

export default function TranscriptSourcePicker({
  transcripts,
  value,
  disabled,
  loading,
  onSearchChange,
  onSelect,
}: TranscriptSourcePickerProps) {
  const completedValue = value?.status === "Completed" ? value : null;
  const options = [
    ...(completedValue ? [completedValue] : []),
    ...transcripts.filter((item) => item.status === "Completed"),
  ].filter(
    (item, index, items) =>
      items.findIndex(
        (candidate) => candidate.transcript_id === item.transcript_id,
      ) === index,
  );

  return (
    <Autocomplete
      disabled={disabled}
      loading={loading}
      loadingText="Loading completed transcripts..."
      openOnFocus
      placeholder="Search completed transcripts"
      options={options}
      value={completedValue}
      getOptionLabel={transcriptLabel}
      isOptionEqualToValue={(option, selected) =>
        option.transcript_id === selected.transcript_id
      }
      onInputChange={(_event, inputValue, reason) => {
        if (reason === "input") onSearchChange(inputValue);
      }}
      onChange={(_event, transcript) => onSelect(transcript)}
      slotProps={{
        listbox: {
          placement: "top-start",
          sx: { maxHeight: 280, overflowY: "auto" },
        },
        option: { sx: { px: 1.5 } },
      }}
      renderOption={(props, transcript) => (
        <AutocompleteOption {...props} key={transcript.transcript_id}>
          <Stack>
            <Typography level="body-sm">{transcript.title}</Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              {new Date(transcript.created_date).toLocaleDateString()} ·{" "}
              {transcript.folder_name || "No folder"}
            </Typography>
          </Stack>
        </AutocompleteOption>
      )}
    />
  );
}
